import { memo, useCallback, useId, useMemo } from "react";

import type { DayOfTheWeek, InputMaybe } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { DAY_OF_THE_WEEK_LIST } from "@/components/features/items/edit/_shared/constants/day-of-the-week";
import {
	HOLIDAY_CHECK_IN_RADIO_OPTIONS,
	HOLIDAY_CHECK_IN_SECTION_DESCRIPTION,
	HOLIDAY_CHECK_IN_SECTION_TITLE,
} from "@/components/features/items/edit/_shared/ui/components/main/questions/stayable-day-of-the-week/holiday-radio-labels";
import { HolidayStayableRadioSubBlock } from "@/components/features/items/edit/_shared/ui/components/main/questions/stayable-day-of-the-week/holiday-stayable-radio-sub-block";

import { FormErrorMessage, FormItem } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxGroupField } from "@/components/ui/form/fields/check-box-field/check-box-group";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props = {
	checkInAvailableDayOfTheWeek: ForArrayType<InputMaybe<DayOfTheWeek[]> | undefined> | undefined;
	rawCheckInAvailableDayOfTheWeek: ForAutoGenerator<DayOfTheWeek, "">[];
	onSetCheckInAvailableDayOfTheWeek: (input: FormOptionType) => void;

	// 祝日設定
	isHolidayCheckInAvailable?: boolean | null;
	onSetIsHolidayCheckInAvailable?: (value: boolean | undefined) => void;
};

export const QuestionPlanCheckInAvailableDayOfTheWeek = memo(
	({
		checkInAvailableDayOfTheWeek,
		rawCheckInAvailableDayOfTheWeek,
		onSetCheckInAvailableDayOfTheWeek: _onSetCheckInAvailableDayOfTheWeek,
		isHolidayCheckInAvailable,
		onSetIsHolidayCheckInAvailable,
	}: Props) => {
		const uniqueID = useId();

		const checkInAvailableDayOfTheWeekOptions = DAY_OF_THE_WEEK_LIST.map((dayOfTheWeek) => {
			return {
				...dayOfTheWeek,
				id: `checkInAvailableDayOfTheWeekOptions_${dayOfTheWeek.value}_${uniqueID}`,
			} satisfies FormOptionType;
		});

		const onSetCheckInAvailableDayOfTheWeek = (_input: CheckedState, item: FormOptionType) => {
			_onSetCheckInAvailableDayOfTheWeek(item);
		};

		const holidayValue = useMemo(() => {
			if (isHolidayCheckInAvailable === true) return "true";
			if (isHolidayCheckInAvailable === false) return "false";
			return "unset";
		}, [isHolidayCheckInAvailable]);

		const handleHolidayChange = useCallback(
			(value: string) => {
				if (!onSetIsHolidayCheckInAvailable) return;
				if (value === "true") onSetIsHolidayCheckInAvailable(true);
				else if (value === "false") onSetIsHolidayCheckInAvailable(false);
				else onSetIsHolidayCheckInAvailable(undefined);
			},
			[onSetIsHolidayCheckInAvailable],
		);

		const title = "チェックイン可能な曜日";

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={title}
				hasError={checkInAvailableDayOfTheWeek?.hasError ?? false}
				isEdited={checkInAvailableDayOfTheWeek?.isEdited ?? false}
				inputChildren={
					<FormItem>
						<CheckboxGroupField
							value={rawCheckInAvailableDayOfTheWeek ?? []}
							options={checkInAvailableDayOfTheWeekOptions}
							handleCheckedChange={onSetCheckInAvailableDayOfTheWeek}
							gridCols={2}
						/>
						<FormErrorMessage>{checkInAvailableDayOfTheWeek?.error}</FormErrorMessage>
						{onSetIsHolidayCheckInAvailable && (
							<HolidayStayableRadioSubBlock
								sectionTitle={HOLIDAY_CHECK_IN_SECTION_TITLE}
								sectionDescription={HOLIDAY_CHECK_IN_SECTION_DESCRIPTION}
								options={HOLIDAY_CHECK_IN_RADIO_OPTIONS}
								value={holidayValue}
								onValueChange={handleHolidayChange}
							/>
						)}
					</FormItem>
				}
			/>
		);
	},
);

QuestionPlanCheckInAvailableDayOfTheWeek.displayName = "QuestionPlanCheckInAvailableDayOfTheWeek";
